import { useTranslation } from '../../../../lib/i18n';
import { messageDir } from '../../../../lib/message-direction';
import { LinkifiedText } from '../../../LinkifiedText';
import { Modal } from '../../../Modal';
import { Button, StatusBadge, Typography } from '../../ui';
import { AgentConversationCard } from '../AgentConversationCard';
import { TaskActivityTimeline } from '../TaskActivityTimeline';
import { TaskFilesSection } from '../TaskFilesSection';
import { TaskSentOutreachSection } from '../TaskSentOutreachSection';
import { useTaskDetailModal } from '../use-task-detail-modal';
import { CancelTaskConfirm } from '../CancelTaskConfirm';
import taskStyles from '../TaskDetailsPanel.module.css';
import styles from '../TaskDetailModal.module.css';

// The wide task-details drill-in: the DS centered <Modal>. The task title is the modal header; the body
// pairs the description with the status badge and shows the agent-activity timeline; the footer carries
// Close and "Resolve with {agent}". Behaviour lives in useTaskDetailModal — this file is only its wide look.
export function TaskDetailDesktop({ taskId }: { taskId: string }) {
  const vm = useTaskDetailModal(taskId);
  const { t } = useTranslation();

  if (vm.kind === 'loading') {
    return (
      <Modal open onClose={vm.close} size="medium">
        <div className={taskStyles.skel}>
          <div className="orion-shimmer" style={{ height: 18, width: '60%' }} />
          <div className="orion-shimmer" style={{ height: 13, width: '90%' }} />
          <div className="orion-shimmer" style={{ height: 13, width: '80%' }} />
        </div>
      </Modal>
    );
  }
  if (vm.kind === 'error') {
    return (
      <Modal open onClose={vm.close} size="medium" title={t('tasks.detail.title')} labelledBy="task-detail-title" headerSeparator>
        <div className={taskStyles.error}>{t('tasks.detail.notFound')}</div>
      </Modal>
    );
  }

  const { task, title, description, agent } = vm;
  return (
    <Modal
      open
      onClose={vm.close}
      size="medium"
      maxHeightPx={650}
      title={title}
      labelledBy="task-detail-title"
      headerSeparator
      footerSeparator
      scrollBody
      footer={
        <>
          {/* Offered only while the task is still open. Outlined-destructive because it opens a confirm
              rather than cancelling on the spot — the filled destructive skin belongs on the real
              confirm, exactly as the connectors' disconnect pair does. */}
          {vm.canCancel && (
            <Button variant="outlined" color="destructive" onClick={vm.onCancel}>
              {t('tasks.cancel.action')}
            </Button>
          )}
          {/* NOT "Close": sitting beside "Resolve with {agent}" that read as closing the TASK. This
              button only dismisses the dialog, and now says so. */}
          <Button variant="outlined" onClick={vm.close}>
            {t('tasks.detail.closeDetails')}
          </Button>
          <Button onClick={vm.onResolve}>{t('tasks.detail.resolveWith', { agent: agent.short })}</Button>
        </>
      }
    >
      {/* The task status + plan, then the produced files, then the agent's own conversation tucked into a
          collapsible card so the modal opens on the plan and reveals the transcript on demand. */}
      <div className={styles.body}>
        <div className={styles.descRow}>
          {description && (
            <Typography variant="p2" component="p" className={styles.desc} dir={messageDir(description)}>
              <LinkifiedText text={description} />
            </Typography>
          )}
          <StatusBadge status={task.status} detail />
        </div>
        <TaskActivityTimeline
          agentId={task.agentId}
          agentName={vm.agentName}
          steps={task.steps}
          currentStep={vm.currentStep}
          statusVariant={vm.statusVariant}
          isLive={vm.isLive}
        />
        <TaskSentOutreachSection outreaches={vm.sentOutreaches} />
        <TaskFilesSection files={vm.files} onOpen={vm.onOpenFile} />
        <AgentConversationCard taskId={taskId} dashboardBuild={!!task.isDashboardBuild} />
        <CancelTaskConfirm flow={vm.cancel} />
      </div>
    </Modal>
  );
}
